// =============================================================================
// Administration des Types de Documents
// =============================================================================
// Permet de personnaliser la liste des types de documents par tenant
// =============================================================================

import { useState, useEffect } from "react";
import {
  FileText,
  Plus,
  Trash2,
  RotateCcw,
  Save,
  CheckCircle2,
  XCircle,
  Edit,
  ChevronDown,
  ChevronRight,
  Shield,
  AlertCircle,
  RefreshCw
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// 🔴🔴 N°747 — L'ÉCRAN RESTE (un artisan gère ses pièces : photos, devis,
// factures), ⛔ mais PAS son vocabulaire d'aides. ⚠️ MESURÉ : la catégorie
// « Aides (MPR/CEE) » et TROIS validations IA (« Mandat MPR », « Attestation sur
// l'honneur CEE », « Cadre Contribution CEE ») étaient proposées à tout tenant.
// 🔑 Régime d'aides DÉCLARÉ — omv (cee) et climat (mpr+cee) les gardent.
import { getRegimeAides } from "../../utils/tenantProfile";
import { enteteTenant } from "../../utils/enteteTenant";

const aDesAides = () => getRegimeAides().length > 0;

//: Ce qui ⛔ n'existe QUE dans un parcours d'aides.
const CATEGORIE_AIDES = "aides";
const VALIDATIONS_AIDES = ["MANDAT_MPR", "AH_CEE", "CC_CEE"];

// Catégories disponibles
const CATEGORIES = [
  { value: "general", label: "Général" },
  { value: "aides", label: "Aides (MPR/CEE)" },
  { value: "administratif", label: "Administratif" },
  { value: "technique", label: "Technique" }
];

// Types de validation IA disponibles
const VALIDATION_TYPES = [
  { value: "_none", label: "Aucune validation IA" },
  { value: "CNI", label: "CNI / Pièce d'identité" },
  { value: "AVIS_IMPOSITION", label: "Avis d'imposition" },
  { value: "JUSTIF_DOMICILE", label: "Justificatif de domicile" },
  { value: "DEVIS", label: "Devis" },
  { value: "FACTURE", label: "Facture" },
  { value: "MANDAT_MPR", label: "Mandat MPR" },
  { value: "AH_CEE", label: "Attestation sur l'honneur CEE" },
  { value: "CC_CEE", label: "Cadre Contribution CEE" },
  { value: "TAXE_FONCIERE", label: "Taxe foncière" }
];

//: Les listes RÉELLEMENT proposées, selon le régime. ⛔ Filtrage d'AFFICHAGE :
//: un type déjà créé en base ⛔ n'est PAS supprimé par ce lot.
const categoriesVisibles = () =>
  aDesAides() ? CATEGORIES : CATEGORIES.filter(c => c.value !== CATEGORIE_AIDES);
const validationsVisibles = () =>
  aDesAides() ? VALIDATION_TYPES
              : VALIDATION_TYPES.filter(v => !VALIDATIONS_AIDES.includes(v.value));

// Types protégés (ne peuvent pas être supprimés)
const PROTECTED_TYPES = ["piece_identite", "devis", "facture", "autre"];

export default function DocumentTypesAdmin({ tenantId }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isCustom, setIsCustom] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  
  // Nouveau type
  const [newType, setNewType] = useState({
    value: "",
    label: "",
    validation_type: "",
    category: "general",
    active: true
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return {
      ...enteteTenant(tenantId),
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  };

  // Charger les types
  useEffect(() => {
    loadTypes();
  }, [tenantId]);

  const loadTypes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/metier/config/document-types/all`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setTypes(data.types || []);
        setIsCustom(data.is_custom);
      }
    } catch (error) {
      console.error("Erreur chargement types:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  // Sauvegarder tous les types
  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/metier/config/document-types`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ types })
      });

      if (res.ok) {
        toast.success("Types de documents sauvegardés");
        setIsCustom(true);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Erreur sauvegarde:", error);
      toast.error("Erreur de communication");
    } finally {
      setSaving(false);
    }
  };

  // Ajouter un nouveau type
  const handleAddType = async () => {
    if (!newType.value || !newType.label) {
      toast.error("Veuillez remplir la clé technique et le libellé");
      return;
    }

    // Vérifier que la clé n'existe pas
    if (types.some(t => t.value === newType.value)) {
      toast.error("Cette clé technique existe déjà");
      return;
    }

    // Ajouter localement
    setTypes([...types, { ...newType }]);
    setShowAddDialog(false);
    setNewType({
      value: "",
      label: "",
      validation_type: "",
      category: "general",
      active: true
    });
    toast.success("Type ajouté (n'oubliez pas de sauvegarder)");
  };

  // Toggle actif/inactif
  const handleToggleActive = (index) => {
    const type = types[index];
    if (PROTECTED_TYPES.includes(type.value) && type.active) {
      toast.error("Ce type est protégé et ne peut pas être désactivé");
      return;
    }

    const updated = [...types];
    updated[index] = { ...updated[index], active: !updated[index].active };
    setTypes(updated);
  };

  // Supprimer un type (soft delete = désactiver)
  const handleDelete = (index) => {
    const type = types[index];
    if (PROTECTED_TYPES.includes(type.value)) {
      toast.error("Ce type est protégé et ne peut pas être supprimé");
      return;
    }

    const updated = [...types];
    updated[index] = { ...updated[index], active: false };
    setTypes(updated);
    toast.info("Type désactivé (sauvegardez pour confirmer)");
  };

  // Réinitialiser aux valeurs par défaut
  const handleReset = async () => {
    try {
      const res = await fetch(`${API}/api/metier/config/document-types/reset`, {
        method: "POST",
        headers: getAuthHeaders()
      });

      if (res.ok) {
        const data = await res.json();
        setTypes(data.types);
        setIsCustom(false);
        toast.success("Types réinitialisés aux valeurs par défaut");
      }
    } catch (error) {
      console.error("Erreur reset:", error);
      toast.error("Erreur lors de la réinitialisation");
    }
  };

  // Modifier le label d'un type
  const handleUpdateLabel = (index, newLabel) => {
    const updated = [...types];
    updated[index] = { ...updated[index], label: newLabel };
    setTypes(updated);
  };

  // Modifier la catégorie
  const handleUpdateCategory = (index, newCategory) => {
    const updated = [...types];
    updated[index] = { ...updated[index], category: newCategory };
    setTypes(updated);
  };

  // Grouper par catégorie
  const typesByCategory = types.reduce((acc, type, index) => {
    const cat = type.category || "general";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push({ ...type, _index: index });
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-[var(--oh-accent-text)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-[var(--oh-text)] font-medium flex items-center gap-2">
              <FileText className="w-5 h-5 text-[var(--oh-accent-text)]" />
              Types de Documents
            </h3>
            <p className="text-[var(--oh-text-muted)] text-sm mt-1">
              Personnalisez la liste des types de documents disponibles lors de l'upload
            </p>
            {isCustom && (
              <Badge className="mt-2 bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]">
                Configuration personnalisée
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-[var(--oh-text-muted)]">
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Réinitialiser
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-[var(--oh-text)]">Réinitialiser aux valeurs par défaut ?</AlertDialogTitle>
                  <AlertDialogDescription className="text-[var(--oh-text-muted)]">
                    Cette action supprimera toutes vos personnalisations et restaurera la liste par défaut.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-[var(--oh-bg-elevated)] text-[var(--oh-text)] border-[var(--oh-border)]">Annuler</AlertDialogCancel>
                  <AlertDialogAction onClick={handleReset} className="bg-red-600 hover:bg-red-700">
                    Réinitialiser
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]">
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter un type
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                <DialogHeader>
                  <DialogTitle className="text-[var(--oh-text)]">Nouveau type de document</DialogTitle>
                  <DialogDescription className="text-[var(--oh-text-muted)]">
                    Créez un nouveau type de document personnalisé
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label className="text-[var(--oh-text-muted)]">Clé technique *</Label>
                    <Input
                      placeholder="ex: attestation_rge"
                      value={newType.value}
                      onChange={(e) => setNewType({ ...newType, value: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                      className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                    />
                    <p className="text-[var(--oh-text-secondary)] text-xs">Lettres minuscules, chiffres et underscores uniquement</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[var(--oh-text-muted)]">Libellé affiché *</Label>
                    <Input
                      placeholder="ex: Attestation RGE"
                      value={newType.label}
                      onChange={(e) => setNewType({ ...newType, label: e.target.value })}
                      className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[var(--oh-text-muted)]">Catégorie</Label>
                    <Select
                      value={newType.category}
                      onValueChange={(v) => setNewType({ ...newType, category: v })}
                    >
                      <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                        {categoriesVisibles().map(cat => (
                          <SelectItem key={cat.value} value={cat.value} className="text-[var(--oh-text)]">
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[var(--oh-text-muted)]">Validation IA</Label>
                    <Select
                      value={newType.validation_type || "_none"}
                      onValueChange={(v) => setNewType({ ...newType, validation_type: v === "_none" ? null : v })}
                    >
                      <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                        {validationsVisibles().map(vt => (
                          <SelectItem key={vt.value} value={vt.value} className="text-[var(--oh-text)]">
                            {vt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                    Annuler
                  </Button>
                  <Button onClick={handleAddType} className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]">
                    Ajouter
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            <Button
              onClick={handleSaveAll}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Sauvegarder
            </Button>
          </div>
        </div>
      </div>

      {/* Liste des types par catégorie */}
      {categoriesVisibles().map(category => {
        const categoryTypes = typesByCategory[category.value] || [];
        if (categoryTypes.length === 0) return null;

        return (
          <Card key={category.value} className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-[var(--oh-text)] flex items-center gap-2">
                {category.label}
                <Badge className="bg-slate-600 text-xs">{categoryTypes.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {categoryTypes.map((type) => (
                  <div
                    key={type.value}
                    className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                      type.active 
                        ? "bg-[var(--oh-bg-elevated)] hover:bg-[var(--oh-bg-elevated)]" 
                        : "bg-[var(--oh-bg-surface)] opacity-60"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Indicateur ON/OFF plus visible */}
                      <button
                        onClick={() => !PROTECTED_TYPES.includes(type.value) && handleToggleActive(type._index)}
                        disabled={PROTECTED_TYPES.includes(type.value)}
                        className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                          type.active 
                            ? "bg-green-500 justify-end" 
                            : "bg-slate-600 justify-start"
                        } ${PROTECTED_TYPES.includes(type.value) ? "cursor-not-allowed" : "cursor-pointer hover:opacity-80"}`}
                      >
                        <span className={`w-5 h-5 rounded-full mx-0.5 transition-colors ${
                          type.active ? "bg-[var(--oh-bg-surface)]" : "bg-slate-400"
                        }`} />
                      </button>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[var(--oh-text)] font-medium">{type.label}</span>
                          {PROTECTED_TYPES.includes(type.value) && (
                            <Badge className="bg-[var(--oh-info-bg)] text-[var(--oh-info-text)] text-xs">
                              <Shield className="w-3 h-3 mr-1" />
                              Système
                            </Badge>
                          )}
                          {type.validation_type && (
                            <Badge className="bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)] text-xs">
                              IA: {type.validation_type}
                            </Badge>
                          )}
                          {type.active ? (
                            <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] text-xs">Actif</Badge>
                          ) : (
                            <Badge className="bg-[var(--oh-error-bg)] text-[var(--oh-error-text)] text-xs">Inactif</Badge>
                          )}
                        </div>
                        <code className="text-[var(--oh-text-secondary)] text-xs">{type.value}</code>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!PROTECTED_TYPES.includes(type.value) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(type._index)}
                          className="text-[var(--oh-error-text)] hover:text-[var(--oh-error-text)] hover:bg-[var(--oh-error-bg)]"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Types inactifs */}
      {types.filter(t => !t.active).length > 0 && (
        <Card className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] border-dashed">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-[var(--oh-text-muted)] flex items-center gap-2">
              <XCircle className="w-4 h-4" />
              Types désactivés
              <Badge className="bg-[var(--oh-bg-elevated)] text-xs">{types.filter(t => !t.active).length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {types.filter(t => !t.active).map((type, idx) => (
                <Badge 
                  key={type.value} 
                  className="bg-[var(--oh-bg-elevated)] text-[var(--oh-text-muted)] cursor-pointer hover:bg-slate-600"
                  onClick={() => {
                    const realIndex = types.findIndex(t => t.value === type.value);
                    handleToggleActive(realIndex);
                  }}
                >
                  {type.label}
                  <RotateCcw className="w-3 h-3 ml-1" />
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
